export interface ChannelSoundHandle { stop(): void; mix(volume: number, pan: number): void }
export interface ChannelSoundBackend {
  duration(url: string): number;
  controlled(url: string, options: { delay: number; offset: number; seconds: number; loop: boolean; volume: number; pan: number }): ChannelSoundHandle | undefined;
}
interface Sequence { clips: string[]; handles: ChannelSoundHandle[]; end?: ChannelSoundHandle; scheduled: boolean }

/** One random start, bed and end per cast, scheduled on the audio clock without gaps. */
export class ChannelSoundSequence {
  private casts = new Map<string, Sequence>();
  private tails = new Set<ChannelSoundHandle>();
  constructor(private backend: ChannelSoundBackend, private pools: readonly string[][], private random = Math.random) {}
  start(id: string) {
    this.stop(id);
    if (this.pools.some(pool => !pool.length)) return;
    this.casts.set(id, { clips: this.pools.map(pool => pool[Math.floor(this.random() * pool.length)]), handles: [], scheduled: false });
  }
  update(id: string, elapsed: number, duration: number, paused: boolean, volume: number, pan: number) {
    const cast = this.casts.get(id); if (!cast) return;
    if (paused) {
      for (const handle of cast.handles) handle.stop();
      cast.handles = []; cast.scheduled = false; return;
    }
    if (!cast.scheduled) {
      const [start, bed, end] = cast.clips, startLength = this.backend.duration(start), endLength = this.backend.duration(end);
      if (!startLength || !endLength || !this.backend.duration(bed)) return;
      // The recordings contain long tails: keep the attack brief so even a two-second cast has a bed,
      // then begin the end with the final hand collapse and let its tail ring after completion.
      const bedAt = Math.min(startLength, .6, duration / 3), endAt = Math.max(bedAt, duration - Math.min(.6, endLength));
      const segments = [[start, 0, bedAt, false], [bed, bedAt, endAt, true], [end, endAt, endAt + endLength, false]] as const;
      for (const [url, from, to, loop] of segments) {
        if (elapsed >= to || to <= from) continue;
        const handle = this.backend.controlled(url, { delay: Math.max(0, from - elapsed), offset: Math.max(0, elapsed - from), seconds: to - Math.max(from, elapsed), loop, volume, pan });
        if (handle) { cast.handles.push(handle); if (url === end && !loop) cast.end = handle; }
      }
      cast.scheduled = cast.handles.length > 0;
    }
    for (const handle of cast.handles) handle.mix(volume, pan);
  }
  stop(id: string, completed = false) {
    const cast = this.casts.get(id);
    for (const handle of cast?.handles ?? []) {
      if (completed && handle === cast?.end) {
        this.tails.add(handle);
        setTimeout(() => this.tails.delete(handle), this.backend.duration(cast.clips[2]) * 1000);
      } else handle.stop();
    }
    this.casts.delete(id);
  }
  clear() { for (const id of this.casts.keys()) this.stop(id); for (const tail of this.tails) tail.stop(); this.tails.clear(); }
}
